import React, { useMemo } from 'react';
import { useParams } from 'react-router';

import { createURL } from '@sm/utils';
import { t } from '@sm/intl';

import useTrackingDetails from '~pricing/hooks/useTrackingDetails';
import { PackageType } from '~shared/types/pricing/package';
import { usePackages } from '~shared/hooks/usePackages';

import { NavRouteParams, NavItemType } from './types';

import ROUTE_CONFIG from './routes';
import NavItem from './NavItem';
import useNavigationStyles from './useStyles';

export default function Navigation(): React.ReactElement {
  const { canonicalPath = PackageType.Teams, subPath } = useParams<NavRouteParams>();
  const { Container, List } = useNavigationStyles();
  const { comparePlans } = usePackages();
  const { pageUtSource } = useTrackingDetails();

  const navItems = useMemo(() => {
    return Object.keys(ROUTE_CONFIG).map((routeName): NavItemType => {
      const { title: TITLE, packageType, path } = ROUTE_CONFIG[routeName as keyof typeof ROUTE_CONFIG];
      const selected =
        canonicalPath === packageType ||
        (canonicalPath === PackageType.Educational && packageType === PackageType.Individual)
          ? 'selected'
          : '';
      const url = createURL(`${path}/${subPath ?? ''}`, { ut_source: pageUtSource });
      return {
        title: t(TITLE),
        selected,
        routeName,
        url,
      };
    });
  }, [canonicalPath, subPath, pageUtSource]);

  return (
    <nav className={Container} data-testid="Navigation__Packages">
      {!comparePlans && (
        <ul className={List} role="tablist">
          {navItems.map(({ routeName, ...rest }) => (
            <NavItem {...rest} key={routeName} />
          ))}
        </ul>
      )}
    </nav>
  );
}
